/**
 * BL628 — entity `scale:` declaration + grid-policy replica resolution.
 *
 * The founder direction (2026-06-29) models scaling the way the platform caps
 * AI tokens: a grid-level policy (per DB tier) + an optional per-entity admin
 * override + a human-friendly entity declaration. The entity author states
 * INTENT (`scale: auto`, `scale: alert`, or an integer for the rare explicit
 * case); the platform decides the live replica count from the grid's policy at
 * deploy time.
 *
 * This module is the PURE resolver — no Firestore, no I/O — so it is unit-
 * testable and shared by the generator (`renderFlatManifests`) and the deployer
 * (which reads the tier policy + override from `platform_settings` and feeds
 * them in). The deployer is the only place that touches the settings doc; the
 * resolution rule itself lives here once.
 *
 * Resolution order (spec §Resolution):
 *   1. admin override (`overrides.by_entity_id.<id>.scale.fixed`) → use that.
 *   2. yaml `scale: <N>` → `min(N, tier.max)`; warn if N > max.
 *   3. yaml `scale: alert` → 1 replica + a SPOF warning.
 *   4. yaml `scale: auto` (or absent) → the tier's `default`.
 *
 * All messages follow §23 voice — plain, specific, no exclamation marks.
 */

/**
 * The entity-side `scale:` value. `auto` (platform picks per grid policy, the
 * default for new entities), `alert` (stay single-replica but warn), or an
 * explicit integer override (clamped to the grid's max).
 */
export type ScaleDeclaration = 'auto' | 'alert' | number;

/** The grid-side policy for one DB tier (`platform_settings.tiers.<tier>.scale`). */
export interface TierScalePolicy {
  /** Replicas an entity gets on this tier with `scale: auto` (or absent). */
  default: number;
  /** Hard ceiling — an explicit `scale: <N>` above this clamps down to it. */
  max: number;
}

/** Per-entity admin override (`platform_settings.overrides.by_entity_id.<id>.scale`). */
export interface ScaleOverride {
  /** Admin-pinned replica count; wins over the entity's yaml declaration. */
  fixed: number;
}

/** The resolved outcome the deployer/generator consumes. */
export interface ResolvedScale {
  /** The effective replica count baked into the web Deployment. */
  replicas: number;
  /** Human advisories (clamp, SPOF) for the deployer log / CLI warn channel. */
  warnings: string[];
  /** True when the entity ends up single-replica (a single point of failure). */
  spof: boolean;
}

/** The lowest replica count any entity can run at. */
export const MIN_SCALE = 1;
/** The highest replica count the `scale: <N>` form accepts (validate-time cap). */
export const MAX_SCALE = 10;

/**
 * The built-in tier scale policies — the §Migration seed. Sane defaults that
 * change behavior for no existing entity (every entity currently runs 1; pool
 * default stays 1). The deployer falls back to these when a settings doc
 * predates BL628 and carries no `tiers.<tier>.scale` block.
 */
export const DEFAULT_TIER_SCALE_POLICIES: Record<
  'pool' | 'dedicated' | 'external',
  TierScalePolicy
> = {
  pool: { default: 1, max: 2 },
  dedicated: { default: 2, max: 5 },
  external: { default: 1, max: 10 },
};

/**
 * §23-voice SPOF advisory surfaced when an entity ends up on a single replica
 * deliberately (`scale: alert`). Shown at plug time through the CLI warn
 * channel; logged server-side by the deployer.
 */
export const SPOF_WARNING =
  'Single replica — this is a single point of failure. One crash or OOM is a ' +
  'full outage, with no second replica to serve. Declare scale: auto to let the ' +
  'grid run highly-available capacity.';

/** Clamp helper — floor to an integer, never below {@link MIN_SCALE}. */
function clampFloor(n: number): number {
  return Math.max(MIN_SCALE, Math.floor(n));
}

/**
 * Resolve the effective replica count from the entity declaration, the grid's
 * tier policy, and an optional per-entity admin override. Pure — the caller
 * supplies the policy + override (the deployer reads them from
 * `platform_settings`; tests pass them inline).
 */
export function resolveScale(
  entityScale: ScaleDeclaration | undefined,
  tierPolicy: TierScalePolicy,
  override?: ScaleOverride,
): ResolvedScale {
  const warnings: string[] = [];

  // 1. Admin override wins outright — regardless of what the yaml declares.
  if (override && typeof override.fixed === 'number') {
    const replicas = clampFloor(override.fixed);
    return { replicas, warnings, spof: replicas === MIN_SCALE };
  }

  const max = clampFloor(tierPolicy.max);

  // 2. Explicit integer override from the yaml — clamp to the grid's ceiling.
  if (typeof entityScale === 'number') {
    const requested = Math.floor(entityScale);
    let replicas = requested;
    if (requested > max) {
      replicas = max;
      warnings.push(
        `scale: ${requested} exceeds this grid's max of ${max} — clamped to ${max}.`,
      );
    }
    replicas = Math.max(MIN_SCALE, replicas);
    return { replicas, warnings, spof: replicas === MIN_SCALE };
  }

  // 3. `alert` — deliberately single-replica, with the SPOF nudge.
  if (entityScale === 'alert') {
    warnings.push(SPOF_WARNING);
    return { replicas: MIN_SCALE, warnings, spof: true };
  }

  // 4. `auto` or absent — the grid's default for this tier.
  const replicas = clampFloor(tierPolicy.default);
  return { replicas, warnings, spof: replicas === MIN_SCALE };
}
